import type { PServed } from '@wui/im'

import { fromPServedUser } from '@/shared/utils/uri'

import { getAbsoluteURL } from './full-url'

export interface InviteTokenSource {
  roomPServed: PServed
  roomToken?: string
  prepareToCall?: boolean
}

const INVITE_TOKEN_ENCODE_DELIMITER = '+'
const INVITE_LOGIN_HASH_RE = /^#\/invite-login\/([A-Za-z0-9+/=]+)$/i

export const getInviteLoginAbsoluteURL = (source: InviteTokenSource): string => {
  const { roomPServed, roomToken } = source
  let inviteToken = `${roomPServed}${INVITE_TOKEN_ENCODE_DELIMITER}${roomToken!}`
  if (source.prepareToCall) {
    inviteToken += `${INVITE_TOKEN_ENCODE_DELIMITER}${source.prepareToCall}`
  }
  return getAbsoluteURL(`/#/invite-login/${btoa(inviteToken)}`)
}

export const getParamsFromInviteToken = (inviteToken: string): InviteTokenSource | null => {
  if (!inviteToken) {
    return null
  }

  try {
    const [roomPServed, roomToken, prepareToCall] = atob(inviteToken).split(INVITE_TOKEN_ENCODE_DELIMITER)
    if (!roomPServed) {
      return null
    }

    return {
      roomPServed,
      roomToken: roomToken!,
      prepareToCall: !!prepareToCall,
    }
  } catch {
    return null
  }
}

const extractInviteHash = (text: string): string | undefined => {
  const trimmed = text.trim()
  if (!trimmed) {
    return undefined
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).hash
    } catch {
      return undefined
    }
  }

  if (trimmed.startsWith('/#/invite-login/')) {
    return trimmed.slice(1)
  }

  if (trimmed.startsWith('#/invite-login/')) {
    return trimmed
  }

  return undefined
}

/** Сообщение целиком — ссылка invite-login (без другого текста). */
export const parseInviteLoginMessage = (raw: string | undefined | null): InviteTokenSource | undefined => {
  if (!raw) {
    return undefined
  }

  const hash = extractInviteHash(raw)
  if (!hash) {
    return undefined
  }

  const match = hash.match(INVITE_LOGIN_HASH_RE)
  const token = match?.[1]
  if (!token) {
    return undefined
  }

  const parsed = getParamsFromInviteToken(token)
  return parsed?.roomPServed ? parsed : undefined

}

export const isInviteLoginMessage = (raw: string | undefined | null): boolean =>
  Boolean(parseInviteLoginMessage(raw))

export const isSameInviteRoom = (
  inviteRoomPServed: string,
  chatPServed: string,
): boolean => {
  if (!inviteRoomPServed || !chatPServed) {
    return false
  }

  if (inviteRoomPServed === chatPServed) {
    return true
  }

  return fromPServedUser(inviteRoomPServed) === fromPServedUser(chatPServed)
}
